import { Terminal, Zap, AlertTriangle, CheckCircle, Copy } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocsPageHeader } from '../../../components/docs/DocsPageHeader';
import { DocScreenshots } from '../../../components/docs/DocScreenshots';

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                     */
/* ------------------------------------------------------------------ */

const BASE_URL_PLACEHOLDER = 'https://api.etherworks.cn';
const API_KEY_PLACEHOLDER  = 'your_api_key_here';
const MODEL_ID_PLACEHOLDER = 'your-model-id';
const IDE_DOCS_UPDATED_AT  = '2026-06-07';

const IDE_LOGOS = {
  cursor:   '/logos/ide/cursor.svg',
  cline:    '/logos/ide/cline.svg',
  claude:   '/logos/ide/claude.svg',
  opencode: '/logos/ide/opencode.svg',
  openai:   '/logos/ide/openai.svg',
} as const;

function LogoChip({ src, alt }: { src: string; alt: string }) {
  return (
    <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white ring-1 ring-black/5 shadow-sm shrink-0">
      <img src={src} alt={alt} className="h-6 w-6 object-contain" loading="lazy" />
    </span>
  );
}

function CopyBlock({ code, label }: { code: string; label?: string }) {
  const { t } = useTranslation(['docs', 'common']);
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group">
      {label && <p className="text-xs text-muted-foreground mb-1">{label}</p>}
      <pre className="bg-zinc-950 text-zinc-200 border border-zinc-800 rounded-xl p-4 text-sm font-mono antialiased overflow-x-auto whitespace-pre-wrap">
        {code}
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-3 right-3 p-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 opacity-0 group-hover:opacity-100 transition"
        title={t('common:copy')}
      >
        {copied ? <CheckCircle className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function StepCard({ step, title, children }: { step: number; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
      <h4 className="text-lg font-semibold text-foreground mb-4">{step}. {title}</h4>
      {children}
    </div>
  );
}

function ConfigTable({ rows }: { rows: { label: string; value: string }[] }) {
  return (
    <div className="space-y-2 text-sm">
      {rows.map(({ label, value }) => (
        <div key={label} className="flex justify-between">
          <span className="text-muted-foreground">{label}:</span>
          <code className="bg-muted px-2 py-1 rounded text-xs">{value}</code>
        </div>
      ))}
    </div>
  );
}

function ImportantNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3">
      <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
      <div className="text-sm text-amber-800">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  IDE Integration Hub                                                */
/* ------------------------------------------------------------------ */

export function IdeIntegrationContent() {
  const { t } = useTranslation('docs');

  const ides = [
    { name: t('ide.cursor.name'), path: '/docs/integrations/cursor', description: t('ide.cursor.desc'), logo: IDE_LOGOS.cursor },
    { name: t('ide.cline.name'), path: '/docs/integrations/cline', description: t('ide.cline.desc'), logo: IDE_LOGOS.cline },
    { name: t('ide.claude_code.name'), path: '/docs/integrations/claude-code', description: t('ide.claude_code.desc'), logo: IDE_LOGOS.claude },
    { name: t('ide.opencode.name'), path: '/docs/integrations/opencode', description: t('ide.opencode.desc'), logo: IDE_LOGOS.opencode },
    { name: t('ide.codex.name'), path: '/docs/integrations/codex', description: t('ide.codex.desc'), logo: IDE_LOGOS.openai },
    { name: t('ide.codex_ide.name'), path: '/docs/integrations/codex-ide', description: t('ide.codex_ide.desc'), logo: IDE_LOGOS.openai },
    { name: t('ide.codex_app.name'), path: '/docs/integrations/codex-app', description: t('ide.codex_app.desc'), logo: IDE_LOGOS.openai },
  ];

  return (
    <div className="space-y-16">
      <DocsPageHeader
        badge={t('ide.badge')}
        title={t('ide.heading')}
        description={t('ide.description')}
        updatedAt={t('ide.updated_at', { date: IDE_DOCS_UPDATED_AT })}
      />

      <section className="space-y-6">
        <div className="flex items-center gap-2 text-primary">
          <Zap className="h-5 w-5" />
          <h3 className="text-2xl font-semibold text-foreground">{t('ide.prerequisites')}</h3>
        </div>
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm space-y-4">
          <p className="text-sm text-muted-foreground">{t('ide.prerequisites_intro')}</p>
          <ul className="list-disc pl-5 space-y-2 text-sm text-muted-foreground">
            <li>{t('ide.prereq_api_key')}</li>
            <li>
              {t('ide.prereq_base_url')}&nbsp;
              <code className="bg-muted px-2 py-1 rounded text-xs">{BASE_URL_PLACEHOLDER}/v1</code>
            </li>
            <li>{t('ide.prereq_model')}</li>
          </ul>
        </div>
      </section>

      <section className="space-y-6">
        <h3 className="text-2xl font-semibold text-foreground">{t('ide.choose_tool')}</h3>
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {ides.map(({ name, path, description, logo }) => (
            <a
              key={name}
              href={path}
              className="rounded-3xl border border-border bg-card p-6 shadow-sm hover:border-indigo-300 hover:shadow-md transition group"
            >
              <div className="flex items-center gap-3">
                <LogoChip src={logo} alt={name} />
                <h4 className="text-lg font-semibold text-foreground group-hover:text-indigo-600 transition">{name}</h4>
              </div>
              <p className="text-sm text-muted-foreground mt-3">{description}</p>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Cursor                                                             */
/* ------------------------------------------------------------------ */

export function CursorGuideContent() {
  const { t } = useTranslation('docs');

  return (
    <div className="space-y-16">
      <DocsPageHeader
        badge={t('ide.cursor.badge')}
        title={t('ide.cursor.heading')}
        description={t('ide.cursor.description')}
        updatedAt={t('ide.updated_at', { date: IDE_DOCS_UPDATED_AT })}
        logo={IDE_LOGOS.cursor}
      />

      <ImportantNote>
        {t('ide.cursor.env_warning')}
      </ImportantNote>

      <section className="space-y-6">
        <div className="flex items-center gap-2 text-primary">
          <Terminal className="h-5 w-5" />
          <h3 className="text-2xl font-semibold text-foreground">{t('ide.cursor.setup_steps')}</h3>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <StepCard step={1} title={t('ide.cursor.step1_title')}>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>{t('ide.cursor.step1_text1')}</p>
              <p>{t('ide.cursor.step1_text2')}</p>
            </div>
          </StepCard>

          <StepCard step={2} title={t('ide.cursor.step2_title')}>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>{t('ide.cursor.step2_text')}</p>
              <ConfigTable rows={[
                { label: t('ide.base_url'), value: `${BASE_URL_PLACEHOLDER}/v1` },
                { label: t('ide.api_key'), value: API_KEY_PLACEHOLDER },
              ]} />
            </div>
          </StepCard>

          <StepCard step={3} title={t('ide.cursor.step3_title')}>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>{t('ide.cursor.step3_text1')}</p>
              <p>{t('ide.cursor.step3_text2')}</p>
              <p>{t('ide.cursor.step3_text3')}</p>
            </div>
          </StepCard>

          <StepCard step={4} title={t('ide.cursor.step4_title')}>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>{t('ide.cursor.step4_text')}</p>
              <p className="text-xs text-muted-foreground">{t('ide.cursor.troubleshooting')}</p>
            </div>
          </StepCard>
        </div>
      </section>

      <DocScreenshots tool="cursor" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Cline                                                              */
/* ------------------------------------------------------------------ */

export function ClineGuideContent() {
  const { t } = useTranslation('docs');

  return (
    <div className="space-y-16">
      <DocsPageHeader
        badge={t('ide.cline.badge')}
        title={t('ide.cline.heading')}
        description={t('ide.cline.description')}
        updatedAt={t('ide.updated_at', { date: IDE_DOCS_UPDATED_AT })}
        logo={IDE_LOGOS.cline}
      />

      <ImportantNote>
        {t('ide.cline.version_warning')}
      </ImportantNote>

      <section className="space-y-6">
        <div className="flex items-center gap-2 text-primary">
          <Terminal className="h-5 w-5" />
          <h3 className="text-2xl font-semibold text-foreground">{t('ide.cursor.setup_steps')}</h3>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <StepCard step={1} title={t('ide.cline.step1_title')}>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>{t('ide.cline.step1_text1')}</p>
              <p>{t('ide.cline.step1_text2')}</p>
            </div>
          </StepCard>

          <StepCard step={2} title={t('ide.cline.step2_title')}>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>{t('ide.cline.step2_text1')}</p>
              <p>{t('ide.cline.step2_text2')}</p>
            </div>
          </StepCard>

          <StepCard step={3} title={t('ide.cline.step3_title')}>
            <div className="space-y-3 text-sm text-muted-foreground">
              <ConfigTable rows={[
                { label: t('ide.base_url'), value: `${BASE_URL_PLACEHOLDER}/v1` },
                { label: t('ide.api_key'), value: API_KEY_PLACEHOLDER },
                { label: t('ide.model_id'), value: MODEL_ID_PLACEHOLDER },
              ]} />
              <p className="mt-2">{t('ide.cline.step3_text')}</p>
            </div>
          </StepCard>

          <StepCard step={4} title={t('ide.cline.step4_title')}>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>{t('ide.cline.step4_text1')}</p>
              <p>{t('ide.cline.step4_text2')}</p>
            </div>
          </StepCard>
        </div>
      </section>

      <DocScreenshots tool="cline" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Claude Code                                                        */
/* ------------------------------------------------------------------ */

export function ClaudeCodeGuideContent() {
  const { t } = useTranslation('docs');
  const settingsJson = `"claudeCode.environmentVariables": [
  {
    "name": "ANTHROPIC_BASE_URL",
    "value": "${BASE_URL_PLACEHOLDER}"
  },
  {
    "name": "ANTHROPIC_AUTH_TOKEN",
    "value": "${API_KEY_PLACEHOLDER}"
  },
  {
    "name": "ANTHROPIC_MODEL",
    "value": "${MODEL_ID_PLACEHOLDER}"
  },
  {
    "name": "ANTHROPIC_SMALL_FAST_MODEL",
    "value": "${MODEL_ID_PLACEHOLDER}"
  },
  {
    "name": "API_TIMEOUT_MS",
    "value": "3000000"
  },
  {
    "name": "CLAUDE_CODE_DISABLE_NONESSENTIAL_TRAFFIC",
    "value": "1"
  }
]`;

  return (
    <div className="space-y-16">
      <DocsPageHeader
        badge={t('ide.claude_code.badge')}
        title={t('ide.claude_code.heading')}
        description={t('ide.claude_code.description')}
        updatedAt={t('ide.updated_at', { date: IDE_DOCS_UPDATED_AT })}
        logo={IDE_LOGOS.claude}
      />

      <ImportantNote>
        {t('ide.claude_code.env_warning')}
      </ImportantNote>

      <section className="space-y-6">
        <div className="flex items-center gap-2 text-primary">
          <Terminal className="h-5 w-5" />
          <h3 className="text-2xl font-semibold text-foreground">{t('ide.claude_code.terminal_setup')}</h3>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <StepCard step={1} title={t('ide.claude_code.step1_title')}>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>{t('ide.claude_code.step1_text')}</p>
            </div>
          </StepCard>

          <StepCard step={2} title={t('ide.claude_code.step2_title')}>
            <div className="space-y-3 text-sm text-muted-foreground">
              <p>{t('ide.claude_code.step2_text')}</p>
              <ConfigTable rows={[
                { label: 'ANTHROPIC_BASE_URL', value: BASE_URL_PLACEHOLDER },
                { label: 'ANTHROPIC_AUTH_TOKEN', value: API_KEY_PLACEHOLDER },
                { label: 'ANTHROPIC_MODEL', value: MODEL_ID_PLACEHOLDER },
                { label: 'ANTHROPIC_SMALL_FAST_MODEL', value: MODEL_ID_PLACEHOLDER },
              ]} />
            </div>
          </StepCard>
        </div>

        <StepCard step={3} title={t('ide.claude_code.step3_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.claude_code.step3_text1')}</p>
            <CopyBlock code="claude" />
            <p>{t('ide.claude_code.step3_text2')}</p>
          </div>
        </StepCard>
      </section>

      <section className="space-y-6">
        <div className="flex items-center gap-2 text-primary">
          <Zap className="h-5 w-5" />
          <h3 className="text-2xl font-semibold text-foreground">{t('ide.claude_code.vscode_extension')}</h3>
        </div>

        <p className="text-sm text-muted-foreground">{t('ide.claude_code.vscode_text')}</p>
        <CopyBlock code={settingsJson} />
      </section>

      <DocScreenshots tool="claude-code" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  OpenCode                                                           */
/* ------------------------------------------------------------------ */

export function OpenCodeGuideContent() {
  const { t } = useTranslation('docs');
  const configJson = `{
  "provider": {
    "openai": {
      "npm": "@ai-sdk/openai",
      "name": "EtherWorks",
      "options": {
        "baseURL": "${BASE_URL_PLACEHOLDER}/v1",
        "apiKey": "${API_KEY_PLACEHOLDER}"
      },
      "models": {
        "${MODEL_ID_PLACEHOLDER}": { "name": "${MODEL_ID_PLACEHOLDER}" }
      }
    }
  }
}`;

  return (
    <div className="space-y-16">
      <DocsPageHeader
        badge={t('ide.opencode.badge')}
        title={t('ide.opencode.heading')}
        description={t('ide.opencode.description')}
        updatedAt={t('ide.updated_at', { date: IDE_DOCS_UPDATED_AT })}
        logo={IDE_LOGOS.opencode}
      />

      <ImportantNote>
        {t('ide.opencode.responses_note')}
      </ImportantNote>

      <section className="space-y-6">
        <div className="flex items-center gap-2 text-primary">
          <Terminal className="h-5 w-5" />
          <h3 className="text-2xl font-semibold text-foreground">{t('ide.cursor.setup_steps')}</h3>
        </div>

        <StepCard step={1} title={t('ide.opencode.step1_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.opencode.step1_text')}</p>
          </div>
        </StepCard>

        <StepCard step={2} title={t('ide.opencode.step2_title')}>
          <p className="text-sm text-muted-foreground mb-4">{t('ide.opencode.step2_text')}</p>
          <CopyBlock code={configJson} label="~/.config/opencode/opencode.json" />
        </StepCard>

        <StepCard step={3} title={t('ide.opencode.step3_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.opencode.step3_text')}</p>
          </div>
        </StepCard>

        <StepCard step={4} title={t('ide.opencode.step4_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.opencode.step4_text')}</p>
          </div>
        </StepCard>
      </section>

      <DocScreenshots tool="opencode" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Codex                                                             */
/* ------------------------------------------------------------------ */

export function CodexGuideContent() {
  const { t } = useTranslation('docs');
  const configToml = `model = "${MODEL_ID_PLACEHOLDER}"
model_provider = "etherworks"

[model_providers.etherworks]
name = "EtherWorks"
base_url = "${BASE_URL_PLACEHOLDER}/v1"
env_key = "ETHERWORKS_API_KEY"
wire_api = "responses"
requires_openai_auth = false`;

  return (
    <div className="space-y-16">
      <DocsPageHeader
        badge={t('ide.codex.badge')}
        title={t('ide.codex.heading')}
        description={t('ide.codex.description')}
        updatedAt={t('ide.updated_at', { date: IDE_DOCS_UPDATED_AT })}
        logo={IDE_LOGOS.openai}
      />

      <ImportantNote>
        {t('ide.codex.env_warning')}
      </ImportantNote>

      <section className="space-y-6">
        <div className="flex items-center gap-2 text-primary">
          <Terminal className="h-5 w-5" />
          <h3 className="text-2xl font-semibold text-foreground">{t('ide.cursor.setup_steps')}</h3>
        </div>

        <StepCard step={1} title={t('ide.codex.step1_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.codex.step1_text')}</p>
            <CopyBlock code="npm install -g @openai/codex" />
          </div>
        </StepCard>

        <StepCard step={2} title={t('ide.codex.step2_title')}>
          <p className="text-sm text-muted-foreground mb-4">{t('ide.codex.step2_text')}</p>
          <CopyBlock code={configToml} label="~/.codex/config.toml" />
          <div className="mt-4">
            <ImportantNote>{t('ide.codex_env_key_note')}</ImportantNote>
          </div>
        </StepCard>

        <StepCard step={3} title={t('ide.codex.step3_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.codex.step3_text')}</p>
            <CopyBlock code={`export ETHERWORKS_API_KEY=${API_KEY_PLACEHOLDER}`} />
          </div>
        </StepCard>

        <StepCard step={4} title={t('ide.codex.step4_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.codex.step4_text')}</p>
            <CopyBlock code="codex" />
          </div>
        </StepCard>
      </section>

      <DocScreenshots tool="codex" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Codex IDE Extension (VS Code / Cursor)                             */
/* ------------------------------------------------------------------ */

export function CodexIdeGuideContent() {
  const { t } = useTranslation('docs');
  const configToml = `model = "${MODEL_ID_PLACEHOLDER}"
model_provider = "etherworks"

[model_providers.etherworks]
name = "EtherWorks"
base_url = "${BASE_URL_PLACEHOLDER}/v1"
env_key = "ETHERWORKS_API_KEY"
wire_api = "responses"
requires_openai_auth = false`;

  return (
    <div className="space-y-16">
      <DocsPageHeader
        badge={t('ide.codex_ide.badge')}
        title={t('ide.codex_ide.heading')}
        description={t('ide.codex_ide.description')}
        updatedAt={t('ide.updated_at', { date: IDE_DOCS_UPDATED_AT })}
        logo={IDE_LOGOS.openai}
      />

      <ImportantNote>
        {t('ide.codex_ide.prereq_note')}
      </ImportantNote>

      <section className="space-y-6">
        <div className="flex items-center gap-2 text-primary">
          <Terminal className="h-5 w-5" />
          <h3 className="text-2xl font-semibold text-foreground">{t('ide.cursor.setup_steps')}</h3>
        </div>

        <StepCard step={1} title={t('ide.codex_ide.step1_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.codex_ide.step1_text')}</p>
            <CopyBlock code="openai.chatgpt" />
          </div>
        </StepCard>

        <StepCard step={2} title={t('ide.codex_ide.step2_title')}>
          <p className="text-sm text-muted-foreground mb-4">{t('ide.codex_ide.step2_text')}</p>
          <CopyBlock code={configToml} label="~/.codex/config.toml" />
          <div className="mt-4">
            <ImportantNote>{t('ide.codex_env_key_note')}</ImportantNote>
          </div>
        </StepCard>

        <StepCard step={3} title={t('ide.codex_ide.step3_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.codex_ide.step3_text')}</p>
            <CopyBlock code={`export ETHERWORKS_API_KEY=${API_KEY_PLACEHOLDER}`} />
          </div>
        </StepCard>

        <StepCard step={4} title={t('ide.codex_ide.step4_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.codex_ide.step4_text')}</p>
          </div>
        </StepCard>
      </section>

      <DocScreenshots tool="codex-ide" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Codex App (desktop)                                                */
/* ------------------------------------------------------------------ */

export function CodexAppGuideContent() {
  const { t } = useTranslation('docs');
  const configToml = `model = "${MODEL_ID_PLACEHOLDER}"
model_provider = "etherworks"

[model_providers.etherworks]
name = "EtherWorks"
base_url = "${BASE_URL_PLACEHOLDER}/v1"
env_key = "ETHERWORKS_API_KEY"
wire_api = "responses"
requires_openai_auth = false`;

  return (
    <div className="space-y-16">
      <DocsPageHeader
        badge={t('ide.codex_app.badge')}
        title={t('ide.codex_app.heading')}
        description={t('ide.codex_app.description')}
        updatedAt={t('ide.updated_at', { date: IDE_DOCS_UPDATED_AT })}
        logo={IDE_LOGOS.openai}
      />

      <ImportantNote>
        {t('ide.codex_app.prereq_note')}
      </ImportantNote>

      <section className="space-y-6">
        <div className="flex items-center gap-2 text-primary">
          <Terminal className="h-5 w-5" />
          <h3 className="text-2xl font-semibold text-foreground">{t('ide.cursor.setup_steps')}</h3>
        </div>

        <StepCard step={1} title={t('ide.codex_app.step1_title')}>
          <p className="text-sm text-muted-foreground mb-4">{t('ide.codex_app.step1_text')}</p>
          <CopyBlock code={configToml} label="~/.codex/config.toml" />
          <div className="mt-4">
            <ImportantNote>{t('ide.codex_env_key_note')}</ImportantNote>
          </div>
        </StepCard>

        <StepCard step={2} title={t('ide.codex_app.step2_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.codex_app.step2_text')}</p>
            <CopyBlock code={`export ETHERWORKS_API_KEY=${API_KEY_PLACEHOLDER}`} />
          </div>
        </StepCard>

        <StepCard step={3} title={t('ide.codex_app.step3_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.codex_app.step3_text')}</p>
            <CopyBlock code="https://chatgpt.com/codex" />
          </div>
        </StepCard>

        <StepCard step={4} title={t('ide.codex_app.step4_title')}>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>{t('ide.codex_app.step4_text')}</p>
          </div>
        </StepCard>
      </section>

      <DocScreenshots tool="codex-app" />
    </div>
  );
}
